import React, { useCallback, useMemo, useEffect } from 'react';
import { Button as MButton, Tooltip, Switch } from '@mantine/core';
import { Text } from '@shakkerai/pro-components';
import styles from './index.modules.scss';
// import flashIcon from './flash2.png';
import powerIcon from './power.svg';
import warningIcon from './warning.svg';
import { switchHandlers, updateVipState, useVipState } from '@shakkerai/pro-store';
import { useUserIdentificationHooks, useCalcPointHooksV2 } from '@shakkerai/pro-commercial';
import { isMainland, isOverseas } from '@shakkerai/pro-utils';
import { metaReport } from '@shakkerai/util-meta-report';
import { translate } from '@shakkerai/util-i18n';

export const Button = (args) => {
  const { children, className, onClick, ...restArgs } = args;

  return (
    <MButton variant="filled" onClick={onClick} className={`${styles.proButton} ${className}`}>
      <Text
        {...restArgs}
        style={{
          height: 'max-content',
        }}
      >
        {children}
      </Text>
    </MButton>
  );
};

// 会员加速按钮
export const MemberSpeedBtn = (props) => {
  // showPowerFlag无论是true还是false都可以进行生图，因此弹窗逻辑不依赖于这个字段
  const { userVipInfo, vipAccelerate: switchChecked } = useVipState();
  const { isVip, remainedPriority, isSpecialUser } = useUserIdentificationHooks(userVipInfo);

  const onSwitchChange = useCallback(() => {
    if (switchChecked) {
      switchHandlers.close();
    } else {
      if (isVip && remainedPriority) {
        // 这里应该是：谁都可以开启，在开始训练的时候做校验
        switchHandlers.open();
      } else {
        if (isSpecialUser) {
          switchHandlers.open(); // 专业会员可以开启，在开始生图的时候切回
        } else {
          updateVipState({
            proPurchaseModalVisible: true,
            currentErrorMsg: '加速次数不足',
          });
        }
      }
    }
  }, [switchChecked, isVip, remainedPriority, isSpecialUser]);

  return (
    <div className="flex items-center gap-[6px]">
      <Switch checked={switchChecked} size="xs" color="#FFBE07" classNames={{}} onChange={onSwitchChange} />
      <div className="text-[#2d2d2d] text-[12px] font-[500] flex-shrink-0">{translate('speed_up')}</div>
    </div>
  );
};

/**
 * 生成器按钮, 这里情况复杂：展示形式有两种，点击行为有两种，展示和点击行为并不一一对应
 * @param {*} args
 * @returns
 */
export const GenerateButton = (args) => {
  const { tooltip, children, className, disabled, onClick, ...restArgs } = args;

  // showPowerFlag无论是true还是false都可以进行生图，因此弹窗逻辑不依赖于这个字段
  const {
    preconsumedPower,
    showPowerFlag: showPowerFlagFromServer,
    userVipInfo,
    vipAccelerate: switchChecked,
  } = useVipState();

  const { isVip, remainedPriority, isSpecialUser } = useUserIdentificationHooks(userVipInfo);

  const { isEnoughForClac } = useCalcPointHooksV2();

  // 是否开启了加速
  const canEnableAcculate = useMemo(() => {
    if (switchChecked) {
      if (isVip && remainedPriority) {
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }, [switchChecked, isVip, remainedPriority]);

  useEffect(() => {
    // 如果是会员且有算力，则默认开启加速
    if (isVip && remainedPriority) {
      switchHandlers.open();
    } else {
      switchHandlers.close();
    }
  }, [isVip, remainedPriority]);

  const handleClick = useCallback(() => {
    if (disabled) return;

    if (isOverseas) {
      if (isEnoughForClac) {
        // 算力充足的情况
        onClick();
      } else {
        // 算力不足的情况
        if (showPowerFlagFromServer) {
          // 普通会员，算力不足
          // 集成付费弹窗逻辑
          updateVipState({
            proPurchaseModalVisible: true,
            currentErrorMsg: '算力不足',
          });
        } else {
          // 专业会员，算力不足
          onClick();
        }
      }
    } else {
      if (!switchChecked || (switchChecked && remainedPriority)) {
        // 没有开启加速或者开启了，但是加速足够
        // 算力充足的情况且加速可用
        if (isEnoughForClac && canEnableAcculate) {
          onClick();
        } else {
          // 算力不足的情况
          if (!showPowerFlagFromServer && canEnableAcculate) {
            onClick();
          } else {
            updateVipState({
              proPurchaseModalVisible: true,
              currentErrorMsg: '算力不足',
            });
          }
        }
      } else {
        // 开启了，但是次数不够了，只有专业会员到这里，其它身份在开启加速时候会拦截
        switchHandlers.close();
        onClick();
      }
    }
    metaReport(`generateBtn`);
  }, [isEnoughForClac, disabled, onClick, showPowerFlagFromServer, canEnableAcculate, switchChecked, remainedPriority]);

  // 展示算力标志
  const showPowerFlag = useMemo(() => {
    return showPowerFlagFromServer;
  }, [showPowerFlagFromServer]);

  // 展示无限生图标志
  const showUnlimitTip = useMemo(() => {
    return !showPowerFlag;
  }, [showPowerFlag]);

  return (
    <div className="flex items-center gap-[14px]">
      {isMainland && <MemberSpeedBtn />}

      <Tooltip disabled={!tooltip} position="top" withArrow label={tooltip}>
        <div className="flex-grow">
          <button
            className={`${
              disabled ? styles.disable : ''
            } ${className} w-full flex justify-center py-2 min-w-[140px] text-center text-white leading-[18px] text-[13px] font-medium bg-[#2c2c2c] hover:bg-[#1d1c1c] cursor-pointer rounded-[2px]`}
            onClick={handleClick}
            {...restArgs}
          >
            {children}
            {showPowerFlag && (
              <span className="ml-[12px] flex items-center">
                <img src={powerIcon} className="w-[10px] h-[14px] mr-[4px] inline" alt="" />
                <span>{preconsumedPower}</span>
              </span>
            )}
          </button>
          {isOverseas && showUnlimitTip && (
            <div className="flex items-center mt-[4px] gap-1 justify-center">
              <img src={warningIcon} alt="" />
              <div className={`${styles.warningTips} relative top-[1px]`}>
                Unlimited low speed image generation mode.
              </div>
            </div>
          )}
        </div>
      </Tooltip>
    </div>
  );
};
